import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Right Hand DMS — signed-in app components (design-mockups/app-rebuild).
 *
 * Styles live in app.css, scoped to `.rhd` like the rest of the library. Pages compose these;
 * page-only layout goes in the page's own CSS file. Anything a second page needs belongs here.
 */ import { Testids } from '@hweeks/kei-costs-ui-testids';
import { createContext, forwardRef, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import './app.css';
const cx = (...parts)=>parts.filter(Boolean).join(' ');
/* ---------- page header ---------- */ /**
 * Page header: vermilion kanji tag + caps eyebrow, Mincho title, subline, faint watermark kanji
 * and the page's primary actions on the right. `card` boxes it (as on the components sheet).
 */ export function PageHeader({ k, eyebrow, title, sub, mark, actions, crumbs, card, help, className, children, ...rest }) {
    return /*#__PURE__*/ _jsxs("header", {
        className: cx('rhd-ph', card && 'rhd-ph--card', className),
        ...rest,
        children: [
            mark && /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                className: "rhd-ph__mark",
                children: mark
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rhd-ph__copy",
                children: [
                    crumbs && /*#__PURE__*/ _jsx("nav", {
                        className: "rhd-crumbs",
                        children: crumbs
                    }),
                    (k || eyebrow) && /*#__PURE__*/ _jsxs("div", {
                        className: "rhd-ph__eyebrow",
                        children: [
                            k && /*#__PURE__*/ _jsx("span", {
                                className: "rhd-ktag",
                                children: k
                            }),
                            eyebrow && /*#__PURE__*/ _jsx("span", {
                                className: "rhd-eyebrow",
                                children: eyebrow
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("h1", {
                        className: "rhd-ph__title",
                        children: title
                    }),
                    sub && /*#__PURE__*/ _jsx("p", {
                        className: "rhd-ph__sub",
                        children: sub
                    }),
                    children
                ]
            }),
            (actions || help) && /*#__PURE__*/ _jsxs("div", {
                className: "rhd-ph__actions",
                children: [
                    actions,
                    help && /*#__PURE__*/ _jsx(HelpLink, {
                        section: help
                    })
                ]
            })
        ]
    });
}
/** Section heading inside a page — Mincho 24px, optional count and a right-hand aside/link. */ export function SectionHead({ title, count, aside, as: Tag = 'h2', id }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rhd-sh",
        children: [
            /*#__PURE__*/ _jsxs(Tag, {
                className: "rhd-sh__title",
                id: id,
                children: [
                    title,
                    count != null && /*#__PURE__*/ _jsx("span", {
                        className: "rhd-sh__count",
                        children: count
                    })
                ]
            }),
            aside && /*#__PURE__*/ _jsx("span", {
                className: "rhd-sh__aside",
                children: aside
            })
        ]
    });
}
/** Small vermilion kanji tag (the 今日 / 入札 label). */ export function KTag({ children, small }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-ktag', small && 'rhd-ktag--sm'),
        children: children
    });
}
export function btnClass({ variant = 'secondary', size = 'md', block, flat }, extra) {
    return cx('rhd-b', variant !== 'secondary' && `rhd-b--${variant}`, size !== 'md' && `rhd-b--${size}`, block && 'rhd-b--block', flat && 'rhd-b--flat', extra);
}
/**
 * App button. primary = amber, secondary = card, paper = quiet cancel, danger = vermilion,
 * ghost = text-only. Disabled renders the dashed "not now" state from the design.
 */ export function Btn({ variant, size, block, flat, className, type = 'button', ...rest }) {
    return /*#__PURE__*/ _jsx("button", {
        type: type,
        className: btnClass({
            variant,
            size,
            block,
            flat
        }, className),
        ...rest
    });
}
/** Router link styled as a button. */ export function BtnLink({ variant, size, block, flat, className, ...rest }) {
    return /*#__PURE__*/ _jsx(Link, {
        className: btnClass({
            variant,
            size,
            block,
            flat
        }, className),
        ...rest
    });
}
/** A button with a small explanatory note under it (e.g. disabled Run now · "one run at a time"). */ export function BtnWithNote({ note, children }) {
    if (!note) return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
    return /*#__PURE__*/ _jsxs("span", {
        className: "rhd-b-note",
        children: [
            children,
            /*#__PURE__*/ _jsx("span", {
                children: note
            })
        ]
    });
}
/** Live "Searching sites… 0:45" button state. */ export function BusyLabel({ children, clock }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-b__dot",
                "aria-hidden": "true"
            }),
            children,
            clock != null && /*#__PURE__*/ _jsx("span", {
                className: "rhd-b__mono",
                children: clock
            })
        ]
    });
}
/** Small caps status tag. `round` gives the pill shape used for car stages. */ export function Tag({ tone = 'rule', round, plain, dashed, strike, dot, className, children, ...rest }) {
    return /*#__PURE__*/ _jsxs("span", {
        className: cx('rhd-tag', `rhd-tag--${tone}`, round && 'rhd-tag--round', plain && 'rhd-tag--plain', dashed && 'rhd-tag--dashed', strike && 'rhd-tag--strike', className),
        ...rest,
        children: [
            dot && /*#__PURE__*/ _jsx("span", {
                className: cx('rhd-tag__dot', dot === 'amber' && 'rhd-tag__dot--amber')
            }),
            children
        ]
    });
}
const STAGE_STYLE = {
    watching: {
        label: 'Watching',
        tone: 'rule'
    },
    bidding: {
        label: 'Bidding',
        tone: 'amber'
    },
    won: {
        label: 'Won',
        tone: 'okSolid'
    },
    importing: {
        label: 'Importing',
        tone: 'aiSolid'
    },
    owned: {
        label: 'In stock',
        tone: 'ink'
    },
    sold: {
        label: 'Sold',
        tone: 'inkSolid'
    },
    lost: {
        label: 'Lost',
        tone: 'beniLine'
    },
    passed: {
        label: 'Passed',
        tone: 'rule',
        dashed: true
    },
    archived: {
        label: 'Archived',
        tone: 'muted'
    }
};
/** The one stage pill used everywhere a car's stage shows. */ export function StageTag({ stage, children, testid }) {
    const s = STAGE_STYLE[stage] ?? {
        label: stage,
        tone: 'rule'
    };
    return /*#__PURE__*/ _jsx(Tag, {
        "data-testid": testid,
        tone: s.tone,
        round: true,
        dashed: s.dashed,
        style: s.dashed ? {
            borderColor: 'var(--ink-2)'
        } : undefined,
        children: children ?? s.label
    });
}
/** Hunt status mark: Active (dot), Paused, Fulfilled ✓, Archived. */ export function HuntStatusTag({ status }) {
    switch(status){
        case 'active':
            return /*#__PURE__*/ _jsx(Tag, {
                tone: "ok",
                dot: true,
                children: "Active"
            });
        case 'fulfilled':
            return /*#__PURE__*/ _jsx(Tag, {
                tone: "inkSolid",
                children: "Fulfilled ✓"
            });
        case 'archived':
            return /*#__PURE__*/ _jsx(Tag, {
                tone: "muted",
                children: "Archived"
            });
        default:
            return /*#__PURE__*/ _jsx(Tag, {
                tone: "rule",
                children: status === 'paused' ? 'Paused' : status
            });
    }
}
/** Auction-site breaker state: Fine / Quirks / Down. */ export function HealthTag({ state }) {
    const tone = state === 'fine' ? 'ok' : state === 'quirks' ? 'amber' : 'beni';
    return /*#__PURE__*/ _jsx(Tag, {
        tone: tone,
        style: {
            fontSize: 10.5,
            padding: '5px 8px'
        },
        children: state === 'fine' ? 'Fine' : state === 'quirks' ? 'Quirks' : 'Down'
    });
}
/** JCD / JDM / PCJ site badge; `internal` for the dashed Ninja-sheet mark. */ export function SiteMark({ children, internal, title }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-site', internal && 'rhd-site--internal'),
        title: title,
        children: children
    });
}
/** Auction grade in a tilted circle; R / RA (repair history) in amber. */ export function GradeMark({ grade, size = 'md', className, style }) {
    const g = String(grade ?? '').trim();
    const repair = /^R/i.test(g);
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-gm', size !== 'md' && `rhd-gm--${size}`, g.length > 2 && 'rhd-gm--long', repair && 'rhd-gm--repair', className),
        style: style,
        title: repair ? 'R/RA = accident or repair history' : undefined,
        children: g || '—'
    });
}
/** Sheet recommendation: Bid / Caution / Avoid. */ export function RecTag({ action, children }) {
    const a = action === 'bid' || action === 'caution' || action === 'avoid' ? action : 'caution';
    const label = a === 'bid' ? 'Bid' : a === 'caution' ? 'Caution' : 'Avoid';
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-rec', `rhd-rec--${a}`),
        children: children ?? label
    });
}
/** "82% match" with a tiny meter. Strong ≥ 75, partial 45–74, poor below. */ export function MatchMeter({ pct }) {
    const p = Math.max(0, Math.min(100, Math.round(pct)));
    const band = p >= 75 ? 'strong' : p >= 45 ? 'partial' : 'poor';
    return /*#__PURE__*/ _jsxs("span", {
        className: cx('rhd-match', band !== 'strong' && 'rhd-match--weak', `rhd-match--${band}`),
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-match__bar",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "rhd-match__fill",
                    style: {
                        width: `${p}%`
                    }
                })
            }),
            p,
            "% match"
        ]
    });
}
/** Highlighted frame number / VIN. */ export function FrameMark({ children }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "rhd-frame",
        children: children
    });
}
/** AC mark: AC / No AC listed / AC unknown. */ export function AcTag({ state }) {
    if (state === 'yes') return /*#__PURE__*/ _jsx(Tag, {
        tone: "ok",
        plain: true,
        style: {
            background: 'transparent',
            fontSize: 11.5
        },
        children: "AC"
    });
    if (state === 'no') return /*#__PURE__*/ _jsx(Tag, {
        tone: "beniLine",
        plain: true,
        style: {
            fontSize: 11.5
        },
        children: "No AC listed"
    });
    return /*#__PURE__*/ _jsx(Tag, {
        tone: "rule",
        plain: true,
        dashed: true,
        style: {
            fontSize: 11.5
        },
        children: "AC unknown"
    });
}
/** Coloured status dot (Needs-you rows). */ export function Dot({ tone }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-dot', tone && tone !== 'ink' && `rhd-dot--${tone}`)
    });
}
/** Nav-style count badge; `hot` = vermilion (needs you), `amber` = work waiting (sheets to get). */ export function Count({ n, hot, amber, className }) {
    return /*#__PURE__*/ _jsx("span", {
        className: cx('rhd-count', hot && 'rhd-count--hot', amber && 'rhd-count--amber', className),
        children: n
    });
}
/* ---------- stage stepper ---------- */ const STEPS = [
    {
        stage: 'watching',
        k: '見',
        label: 'Watching'
    },
    {
        stage: 'bidding',
        k: '札',
        label: 'Bidding'
    },
    {
        stage: 'won',
        k: '落',
        label: 'Won'
    },
    {
        stage: 'importing',
        k: '輸',
        label: 'Importing'
    },
    {
        stage: 'owned',
        k: '庫',
        label: 'In stock'
    },
    {
        stage: 'sold',
        k: '売',
        label: 'Sold'
    }
];
/** An individual's car ends at home: no stock, no sale. */ const HOME_STEPS = [
    ...STEPS.slice(0, 4),
    {
        stage: 'owned',
        k: '家',
        label: 'Home'
    }
];
/**
 * The stage stepper with the "Ownership starts at Won" bracket: six stops to Sold, or with
 * `home` (an individual's own car, not for resale) five, ending at Home.
 */ export function StageStepper({ current, when, tip, home }) {
    const steps = home ? HOME_STEPS : STEPS;
    const idx = Math.max(0, // A sold car always shows the six stops (it was resold).
    steps.findIndex((s)=>s.stage === current));
    return /*#__PURE__*/ _jsx("div", {
        className: cx('rhd-stepper', home && 'rhd-stepper--home'),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "rhd-stepper__grid",
            children: [
                steps.map((s, i)=>{
                    const state = i < idx ? 'done' : i === idx ? 'now' : 'todo';
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "rhd-stepper__stop",
                        "aria-current": state === 'now' ? 'step' : undefined,
                        children: [
                            i < steps.length - 1 && /*#__PURE__*/ _jsx("span", {
                                className: cx('rhd-stepper__line', i >= idx && 'rhd-stepper__line--todo')
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: cx('rhd-stepper__dot', state === 'now' && 'rhd-stepper__dot--now', state === 'todo' && 'rhd-stepper__dot--todo'),
                                children: s.k
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: cx('rhd-stepper__label', state === 'todo' && 'rhd-stepper__label--todo'),
                                children: s.label
                            }),
                            when?.[s.stage] != null && /*#__PURE__*/ _jsx("span", {
                                className: "rhd-stepper__when",
                                children: when[s.stage]
                            })
                        ]
                    }, s.stage);
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "rhd-stepper__own",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "rhd-stepper__own-bracket"
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "rhd-stepper__own-label",
                            children: [
                                /*#__PURE__*/ _jsx("b", {
                                    children: "所有"
                                }),
                                "Ownership starts at Won",
                                tip
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
/* ---------- panels ---------- */ /** Card panel with the ink border + 6px print shadow. */ export function Panel({ flat, className, ...rest }) {
    return /*#__PURE__*/ _jsx("section", {
        className: cx('rhd-panel', flat && 'rhd-panel--flat', className),
        ...rest
    });
}
/** Indigo woodgrain header band for a panel ("要 NEEDS YOU 6 … OLDEST FIRST"). */ export function PanelBand({ k, children, aside }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rhd-panel__band",
        children: [
            k && /*#__PURE__*/ _jsx("span", {
                className: "rhd-panel__band-k",
                children: k
            }),
            children,
            aside && /*#__PURE__*/ _jsx("span", {
                className: "rhd-panel__band-aside",
                children: aside
            })
        ]
    });
}
/** Plain panel header: Mincho title with optional kanji tag and actions. */ export function PanelHead({ k, title, actions, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rhd-panel__head",
        children: [
            /*#__PURE__*/ _jsxs("h3", {
                className: "rhd-panel__title",
                children: [
                    k && /*#__PURE__*/ _jsx(KTag, {
                        small: true,
                        children: k
                    }),
                    title
                ]
            }),
            children,
            actions
        ]
    });
}
/** The four-cell figures strip (Today). */ export function Figures({ items }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "rhd-figs",
        children: items.map((f, i)=>/*#__PURE__*/ _jsxs("div", {
                className: "rhd-fig",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-fig__label",
                        children: f.label
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-fig__value",
                        children: f.value
                    }),
                    f.note != null && /*#__PURE__*/ _jsx("span", {
                        className: "rhd-fig__note",
                        children: f.note
                    })
                ]
            }, i))
    });
}
/** Two-column label / value list. */ export function KeyValues({ rows }) {
    return /*#__PURE__*/ _jsx("dl", {
        className: "rhd-kv",
        children: rows.map(([k, v], i)=>/*#__PURE__*/ _jsxs("div", {
                style: {
                    display: 'contents'
                },
                children: [
                    /*#__PURE__*/ _jsx("dt", {
                        children: k
                    }),
                    /*#__PURE__*/ _jsx("dd", {
                        children: v ?? '—'
                    })
                ]
            }, i))
    });
}
/* ---------- form controls ---------- */ /** Label + control + hint/error, stacked. Pass the control as children. */ export function AField({ label, hint, error, children, className, style, as = 'label', errorTestId }) {
    const Tag = as;
    return /*#__PURE__*/ _jsxs(Tag, {
        className: cx('rhd-f', className),
        style: style,
        children: [
            label != null && /*#__PURE__*/ _jsx("span", {
                children: label
            }),
            children,
            error ? /*#__PURE__*/ _jsx("span", {
                className: "rhd-f__err",
                role: "alert",
                "data-testid": errorTestId,
                children: error
            }) : hint ? /*#__PURE__*/ _jsx("span", {
                className: "rhd-f__hint",
                children: hint
            }) : null
        ]
    });
}
export const AInput = /*#__PURE__*/ forwardRef(function AInput({ className, small, mono, invalid, ...rest }, ref) {
    return /*#__PURE__*/ _jsx("input", {
        ref: ref,
        className: cx('rhd-in', small && 'rhd-in--sm', mono && 'rhd-in--mono', className),
        "aria-invalid": invalid || undefined,
        ...rest
    });
});
export const ATextarea = /*#__PURE__*/ forwardRef(function ATextarea({ className, ...rest }, ref) {
    return /*#__PURE__*/ _jsx("textarea", {
        ref: ref,
        className: cx('rhd-in', className),
        ...rest
    });
});
export function ASelect({ className, small, style, ...rest }) {
    return /*#__PURE__*/ _jsxs("span", {
        className: "rhd-sel",
        style: style,
        children: [
            /*#__PURE__*/ _jsx("select", {
                className: cx('rhd-in', small && 'rhd-in--sm', className),
                ...rest
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-sel__arrow",
                "aria-hidden": "true",
                children: "▾"
            })
        ]
    });
}
/** Input with a fixed prefix cell (¥, $). */ export function AffixInput({ prefix, small, className, ...rest }) {
    return /*#__PURE__*/ _jsxs("span", {
        className: cx('rhd-affix', small && 'rhd-affix--sm', className),
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-affix__pre",
                children: prefix
            }),
            /*#__PURE__*/ _jsx("input", {
                ...rest
            })
        ]
    });
}
/** Segmented toggle (Any / MT / AT / CVT). */ export function Segmented({ options, value, onChange, label }) {
    return(// biome-ignore lint/a11y/useSemanticElements: a pressed-button group, not a form fieldset
    /*#__PURE__*/ _jsx("div", {
        className: "rhd-seg",
        role: "group",
        "aria-label": label,
        children: options.map((o)=>/*#__PURE__*/ _jsx("button", {
                type: "button",
                className: "rhd-seg__opt",
                "aria-pressed": o.value === value,
                onClick: ()=>onChange(o.value),
                children: o.label
            }, o.value))
    }));
}
/** Two or more choice cards with a title and a line of explanation ("For: My shop / A customer"). */ export function ChoiceCards({ options, value, onChange, label }) {
    return /*#__PURE__*/ _jsx("fieldset", {
        className: "rhd-choices",
        "aria-label": label,
        children: options.map((o)=>/*#__PURE__*/ _jsxs("button", {
                type: "button",
                "data-testid": o.testid,
                className: "rhd-choice",
                "aria-pressed": o.value === value,
                onClick: ()=>onChange(o.value),
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "rhd-choice__title",
                        children: [
                            o.title,
                            o.value === value ? ' ✓' : ''
                        ]
                    }),
                    o.desc && /*#__PURE__*/ _jsx("span", {
                        className: "rhd-choice__desc",
                        children: o.desc
                    })
                ]
            }, o.value))
    });
}
/** Filter chip with an optional count. `mono` for grades, `repair` for R/RA. */ export function FilterChip({ active, count, mono, repair, className, children, ...rest }) {
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        "aria-pressed": !!active,
        className: cx('rhd-fchip', mono && 'rhd-fchip--mono', repair && 'rhd-fchip--repair', className),
        ...rest,
        children: [
            children,
            count != null && /*#__PURE__*/ _jsx("span", {
                className: "rhd-fchip__n",
                children: count
            })
        ]
    });
}
export function FilterChips({ children }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "rhd-fchips",
        children: children
    });
}
/** Square ink checkbox with its label. */ export function Checkbox({ label, className, ...rest }) {
    return /*#__PURE__*/ _jsxs("label", {
        className: cx('rhd-check', className),
        children: [
            /*#__PURE__*/ _jsx("input", {
                type: "checkbox",
                ...rest
            }),
            /*#__PURE__*/ _jsx("span", {
                children: label
            })
        ]
    });
}
/** Square on/off switch. */ export function Switch({ checked, onChange, label, disabled, ariaLabel, testid }) {
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        role: "switch",
        "aria-checked": checked,
        "aria-label": ariaLabel,
        "data-testid": testid,
        className: "rhd-switch",
        disabled: disabled,
        onClick: ()=>onChange(!checked),
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-switch__track",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "rhd-switch__knob"
                })
            }),
            label
        ]
    });
}
/* ---------- tabs ---------- */ /** Ledger index tabs — the active one joins the panel below it. */ export function Tabs({ tabs, value, onChange, label }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "rhd-tabs",
        role: "tablist",
        "aria-label": label,
        children: tabs.map((t)=>/*#__PURE__*/ _jsxs("button", {
                type: "button",
                role: "tab",
                "aria-selected": t.value === value,
                className: "rhd-tab",
                "data-testid": t.testid,
                onClick: ()=>onChange(t.value),
                children: [
                    t.label,
                    t.count != null && /*#__PURE__*/ _jsxs("span", {
                        className: "rhd-tab__n",
                        children: [
                            "(",
                            t.count,
                            ")"
                        ]
                    })
                ]
            }, t.value))
    });
}
export function TabPanel({ className, ...rest }) {
    return /*#__PURE__*/ _jsx("div", {
        role: "tabpanel",
        className: cx('rhd-tabpanel', className),
        ...rest
    });
}
export const PhotoImgContext = /*#__PURE__*/ createContext((p)=>/*#__PURE__*/ _jsx("img", {
        src: p.src,
        alt: p.alt,
        loading: "lazy",
        onError: p.onError
    }));
export function Photo({ src, alt = '', children, className, style }) {
    const renderImg = useContext(PhotoImgContext);
    // A dead auction-photo link falls back to the striped no-photo ground.
    const [brokenSrc, setBrokenSrc] = useState(null);
    return /*#__PURE__*/ _jsxs("div", {
        className: cx('rhd-photo', className),
        style: style,
        children: [
            src && brokenSrc !== src ? renderImg({
                src,
                alt,
                onError: ()=>setBrokenSrc(src)
            }) : src ? null : /*#__PURE__*/ _jsx("span", {
                className: "rhd-photo__none",
                children: "no photo"
            }),
            children
        ]
    });
}
/** Conservative / Target / Aggressive bid range strip, optionally with a "Max bid" cell. */ export function BidRange({ cells, badge }) {
    const cols = cells.reduce((n, c)=>n + (c.span ?? 1), 0);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            badge && /*#__PURE__*/ _jsx("div", {
                className: "rhd-bidrange__badge",
                children: badge
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "rhd-bidrange",
                "data-cells": cols,
                style: {
                    '--cells': cols
                },
                children: cells.map((c, i)=>/*#__PURE__*/ _jsxs("div", {
                        "data-testid": c.testid,
                        "data-span": c.span && c.span > 1 ? c.span : undefined,
                        style: c.span && c.span > 1 ? {
                            gridColumn: `span ${c.span}`
                        } : undefined,
                        className: cx('rhd-bidrange__cell', c.target && 'rhd-bidrange__cell--target', c.max && 'rhd-bidrange__cell--max', c.muted && 'rhd-bidrange__cell--muted'),
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-bidrange__label",
                                children: c.label
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-bidrange__yen",
                                children: c.yen
                            }),
                            c.usd != null && /*#__PURE__*/ _jsx("span", {
                                className: "rhd-bidrange__usd",
                                children: c.usd
                            }),
                            c.profit != null && /*#__PURE__*/ _jsx("span", {
                                className: "rhd-bidrange__profit",
                                children: c.profit
                            })
                        ]
                    }, c.key ?? (typeof c.label === 'string' ? c.label : i)))
            })
        ]
    });
}
/* ---------- feedback ---------- */ /** Full-width banner: info (indigo), warn (amber), error (vermilion), ok. */ export function Banner({ tone = 'info', k, title, children, actions, className, role, testid }) {
    const glyph = k ?? (tone === 'warn' ? '注' : tone === 'error' ? '誤' : tone === 'ok' ? '済' : '報');
    return /*#__PURE__*/ _jsxs("div", {
        className: cx('rhd-banner', tone !== 'info' && `rhd-banner--${tone}`, className),
        role: role ?? (tone === 'error' ? 'alert' : undefined),
        "data-testid": testid,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-banner__k",
                children: glyph
            }),
            /*#__PURE__*/ _jsxs("span", {
                className: "rhd-banner__text",
                children: [
                    title && /*#__PURE__*/ _jsx("b", {
                        className: "rhd-banner__title",
                        children: title
                    }),
                    children && /*#__PURE__*/ _jsx("span", {
                        className: title ? 'rhd-banner__body' : undefined,
                        children: children
                    })
                ]
            }),
            actions && /*#__PURE__*/ _jsx("span", {
                className: "rhd-banner__actions",
                children: actions
            })
        ]
    });
}
/** Seigaiha-filled progress bar. */ export function Progress({ value, max = 100, label }) {
    const pct = max > 0 ? Math.max(0, Math.min(100, value / max * 100)) : 0;
    return /*#__PURE__*/ _jsx("div", {
        className: "rhd-progress",
        role: "progressbar",
        "aria-label": label,
        "aria-valuenow": value,
        "aria-valuemin": 0,
        "aria-valuemax": max,
        children: /*#__PURE__*/ _jsx("span", {
            className: "rhd-progress__fill",
            style: {
                width: `${pct}%`
            }
        })
    });
}
/** Four-up tally under a progress bar (Should buy / Maybe / Don't buy / Errors). */ export function Tally({ items }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "rhd-tally",
        style: {
            gridTemplateColumns: `repeat(${items.length}, 1fr)`
        },
        children: items.map((t, i)=>/*#__PURE__*/ _jsxs("div", {
                className: "rhd-tally__cell",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-tally__n",
                        style: t.color ? {
                            color: t.color
                        } : undefined,
                        children: t.n
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rhd-tally__label",
                        children: t.label
                    })
                ]
            }, i))
    });
}
/** Dashed empty state with a 無-style seal. */ export function EmptyState({ k = '無', title, children, action, compact, className, testid }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: cx('rhd-empty', compact && 'rhd-empty--compact', className),
        "data-testid": testid,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-empty__seal",
                children: k
            }),
            /*#__PURE__*/ _jsx("h3", {
                className: "rhd-empty__title",
                children: title
            }),
            children && /*#__PURE__*/ _jsx("p", {
                className: "rhd-empty__body",
                children: children
            }),
            action
        ]
    });
}
/** Flat washi skeleton line (no shimmer). */ export function Skel({ w = '100%', h = 12, style }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "rhd-skel",
        style: {
            width: w,
            height: h,
            ...style
        }
    });
}
/** Skeleton lot/result card. */ export function SkelCard({ photo = true }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rhd-panel",
        "aria-busy": "true",
        style: {
            display: 'flex',
            flexDirection: 'column'
        },
        children: [
            photo && /*#__PURE__*/ _jsx("div", {
                className: "rhd-skel rhd-skel--block",
                style: {
                    borderBottom: '1.5px solid var(--ink)'
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    padding: 14,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 9
                },
                children: [
                    /*#__PURE__*/ _jsx(Skel, {
                        w: "70%",
                        h: 14
                    }),
                    /*#__PURE__*/ _jsx(Skel, {
                        w: "45%",
                        h: 11
                    }),
                    /*#__PURE__*/ _jsx(Skel, {
                        w: "58%",
                        h: 11
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        style: {
                            display: 'flex',
                            gap: 6,
                            paddingTop: 4
                        },
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-skel-btn"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-skel-btn"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "rhd-skel-btn"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
/**
 * Something the design shows that the app can't do yet: dashed border + "Coming soon" tag,
 * content dimmed and inert. Use `inline` around a single control.
 */ export function ComingSoon({ children, inline, label = 'Coming soon', className, style }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: cx('rhd-soon', inline && 'rhd-soon--inline', className),
        style: style,
        "aria-disabled": "true",
        children: [
            /*#__PURE__*/ _jsxs("span", {
                className: "rhd-soon__tag",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        "aria-hidden": "true",
                        children: "近"
                    }),
                    label
                ]
            }),
            children && // `inert` keeps the preview out of the tab order (React 18 has no typed prop for it).
            /*#__PURE__*/ _jsx("div", {
                className: "rhd-soon__content",
                inert: '',
                children: children
            })
        ]
    });
}
const ToastCtx = /*#__PURE__*/ createContext(()=>{});
/** Toasts: `const toast = useToast(); toast({ tone: 'ink', k: '札', text: 'Bid recorded' })`. */ export function useToast() {
    return useContext(ToastCtx);
}
export function ToastProvider({ children, testids, theme }) {
    const [items, setItems] = useState([]);
    const seq = useRef(0);
    const dismiss = useCallback((id)=>setItems((cur)=>cur.filter((x)=>x.id !== id)), []);
    const push = useCallback(({ ms, ...t })=>{
        seq.current += 1;
        const id = seq.current;
        setItems((cur)=>[
                ...cur.slice(-2),
                {
                    ...t,
                    id
                }
            ]);
        // Errors and toasts with an action stay long enough to read and act on.
        const life = ms ?? (t.tone === 'error' || t.action ? 8000 : 4000);
        window.setTimeout(()=>dismiss(id), life);
    }, [
        dismiss
    ]);
    return /*#__PURE__*/ _jsxs(ToastCtx.Provider, {
        value: push,
        children: [
            children,
            /*#__PURE__*/ _jsx(ToastScope, {
                theme: theme,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "rhd-toasts",
                    "data-testid": testids?.stack,
                    children: items.map((t)=>/*#__PURE__*/ _jsxs("div", {
                            role: t.tone === 'error' ? 'alert' : 'status',
                            "data-testid": testids?.toast,
                            "data-tone": t.tone,
                            className: cx('rhd-toast', t.tone !== 'ok' && `rhd-toast--${t.tone}`),
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "rhd-toast__k",
                                    "aria-hidden": "true",
                                    children: t.k ?? (t.tone === 'error' ? '誤' : '✓')
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "rhd-toast__text",
                                    children: t.text
                                }),
                                t.action && /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    className: "rhd-toast__act",
                                    "data-testid": testids?.action,
                                    onClick: ()=>{
                                        t.action?.onClick();
                                        dismiss(t.id);
                                    },
                                    children: t.action.label
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    className: "rhd-toast__x",
                                    "aria-label": "Dismiss",
                                    onClick: ()=>dismiss(t.id),
                                    children: "×"
                                })
                            ]
                        }, t.id))
                })
            })
        ]
    });
}
function ToastScope({ theme, children }) {
    if (theme === undefined) return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
    return /*#__PURE__*/ _jsx("div", {
        className: "rhd rhd-portal",
        "data-theme": theme,
        children: children
    });
}
/* ---------- modal ---------- */ /**
 * Modal dialog over the dimmed backdrop. Escape and a backdrop click close it.
 * Renders in place (position: fixed) so it stays inside the .rhd theme scope.
 */ export function Modal({ open = true, onClose, k, title, children, footer, size = 'sm', labelledBy, className, testid }) {
    const autoId = useId();
    const titleId = labelledBy ?? `${autoId}-title`;
    const dialogRef = useRef(null);
    useEffect(()=>{
        if (!open) return;
        const onKey = (e)=>{
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return ()=>window.removeEventListener('keydown', onKey);
    }, [
        open,
        onClose
    ]);
    // Focus moves into the dialog when it opens and back to what opened it when it closes.
    useEffect(()=>{
        if (!open) return;
        const opener = document.activeElement;
        dialogRef.current?.focus({
            preventScroll: true
        });
        return ()=>{
            if (opener?.isConnected) opener.focus({
                preventScroll: true
            });
        };
    }, [
        open
    ]);
    if (!open) return null;
    return /*#__PURE__*/ _jsx(OverlayPortal, {
        children: /*#__PURE__*/ _jsx("div", {
            className: "rhd-overlay",
            onClick: (e)=>{
                if (e.target === e.currentTarget) onClose();
            },
            children: /*#__PURE__*/ _jsxs("div", {
                ref: dialogRef,
                tabIndex: -1,
                role: "dialog",
                "data-testid": testid,
                "aria-modal": "true",
                "aria-labelledby": titleId,
                className: cx('rhd-modal', size !== 'sm' && `rhd-modal--${size}`, className),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rhd-modal__head",
                        children: [
                            k && /*#__PURE__*/ _jsx(KTag, {
                                small: true,
                                children: k
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                id: titleId,
                                className: "rhd-modal__title",
                                children: title
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                className: "rhd-modal__x",
                                "aria-label": "Close",
                                onClick: onClose,
                                children: "×"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "rhd-modal__body",
                        children: children
                    }),
                    footer && /*#__PURE__*/ _jsx("div", {
                        className: "rhd-modal__foot",
                        children: footer
                    })
                ]
            })
        })
    });
}
/**
 * Renders an overlay at the app root (still inside the .rhd theme scope) so nothing in the page —
 * a positioned card, the sticky phone bar — can stack above it. Falls back to in place outside
 * the app shell.
 */ export function OverlayPortal({ children }) {
    const host = typeof document === 'undefined' ? null : document.querySelector('.rhd-app');
    return host ? /*#__PURE__*/ createPortal(children, host) : children;
}
/* ---------- drop zone ---------- */ /** Dashed file drop zone with a "Choose files" button. */ export function DropZone({ onFiles, accept, multiple = true, k = '書', title = 'Drop files here', sub, buttonLabel = 'Choose files', disabled, inputTestid }) {
    const [over, setOver] = useState(false);
    const input = useRef(null);
    const take = (list)=>{
        if (!list || disabled) return;
        const files = Array.from(list);
        if (files.length) onFiles(multiple ? files : files.slice(0, 1));
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: cx('rhd-drop', over && 'rhd-drop--over'),
        onDragOver: (e)=>{
            e.preventDefault();
            setOver(true);
        },
        onDragLeave: ()=>setOver(false),
        onDrop: (e)=>{
            e.preventDefault();
            setOver(false);
            take(e.dataTransfer.files);
        },
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-drop__k",
                children: k
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "rhd-drop__title",
                children: title
            }),
            sub && /*#__PURE__*/ _jsx("span", {
                className: "rhd-drop__sub",
                children: sub
            }),
            /*#__PURE__*/ _jsx(Btn, {
                size: "sm",
                onClick: ()=>input.current?.click(),
                disabled: disabled,
                children: buttonLabel
            }),
            /*#__PURE__*/ _jsx("input", {
                ref: input,
                type: "file",
                hidden: true,
                accept: accept,
                multiple: multiple,
                "data-testid": inputTestid,
                onChange: (e)=>{
                    take(e.target.files);
                    e.target.value = '';
                }
            })
        ]
    });
}
export const HELP_PATH = '/app/help';
/**
 * Link to a section of the Help guide: a round "?" by default, or worded when given children
 * ("How hunts work").
 */ export function HelpLink({ section, children, label = 'Help for this page', className }) {
    const worded = children != null;
    return /*#__PURE__*/ _jsx(Link, {
        to: `${HELP_PATH}#${section}`,
        "data-testid": Testids.app.help.link(section),
        className: cx('rhd-helplink', worded && 'rhd-helplink--text', className),
        "aria-label": worded ? undefined : label,
        title: worded ? undefined : label,
        children: worded ? children : /*#__PURE__*/ _jsx("span", {
            "aria-hidden": "true",
            children: "?"
        })
    });
}
/**
 * A small "?" button with a short plain-text explanation. Shows on hover, keyboard focus or a
 * tap; Escape, blur or a tap elsewhere hides it. While shown, the bubble is the button's
 * aria-describedby. The bubble is portalled with fixed positioning so a clipped card or the page
 * header can't cut it off, and it stays inside the viewport.
 */ export function HelpTip({ children, label = 'What is this?', name, className }) {
    const id = useId();
    const bubbleId = `${id}-tip`;
    const btn = useRef(null);
    const bubble = useRef(null);
    const hideTimer = useRef(undefined);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const show = useCallback(()=>{
        window.clearTimeout(hideTimer.current);
        setOpen(true);
    }, []);
    const hide = useCallback(()=>{
        window.clearTimeout(hideTimer.current);
        setOpen(false);
    }, []);
    // A moment to move the pointer onto the bubble; it stays open while hovered.
    const hideSoon = useCallback(()=>{
        window.clearTimeout(hideTimer.current);
        hideTimer.current = window.setTimeout(()=>setOpen(false), 150);
    }, []);
    useEffect(()=>()=>window.clearTimeout(hideTimer.current), []);
    // Above the button, or below when there's no room; clamped to the viewport.
    const place = useCallback(()=>{
        const b = btn.current?.getBoundingClientRect();
        if (!b) return;
        const w = bubble.current?.offsetWidth ?? 280;
        const h = bubble.current?.offsetHeight ?? 80;
        const left = Math.max(12, Math.min(b.left + b.width / 2 - w / 2, window.innerWidth - w - 12));
        const below = b.top - h - 10 < 8;
        setPos({
            top: below ? b.bottom + 8 : b.top - h - 8,
            left,
            below
        });
    }, []);
    // Measure before paint, once the bubble has rendered at its real size.
    useLayoutEffect(()=>{
        if (open) place();
        else setPos(null);
    }, [
        open,
        place
    ]);
    useEffect(()=>{
        if (!open) return;
        const onKey = (e)=>{
            if (e.key !== 'Escape') return;
            // Close the tip, not the dialog it may sit in (Modal listens on window).
            e.stopPropagation();
            hide();
        };
        const onDown = (e)=>{
            const t = e.target;
            if (!btn.current?.contains(t) && !bubble.current?.contains(t)) hide();
        };
        window.addEventListener('keydown', onKey, true);
        document.addEventListener('pointerdown', onDown);
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return ()=>{
            window.removeEventListener('keydown', onKey, true);
            document.removeEventListener('pointerdown', onDown);
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [
        open,
        place,
        hide
    ]);
    return /*#__PURE__*/ _jsxs("span", {
        className: cx('rhd-helptip', className),
        children: [
            /*#__PURE__*/ _jsx("span", {
                ref: btn,
                role: "button",
                tabIndex: 0,
                className: "rhd-helptip__btn",
                "aria-label": label,
                "aria-expanded": open,
                "aria-describedby": open ? bubbleId : undefined,
                "data-testid": name ? Testids.app.help.tip(name) : undefined,
                onMouseEnter: show,
                onMouseLeave: hideSoon,
                onFocus: show,
                onBlur: hide,
                onClick: (e)=>{
                    // Inside a <label>, don't let the tap reach the field it labels.
                    e.preventDefault();
                    e.stopPropagation();
                    show();
                },
                onKeyDown: (e)=>{
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        show();
                    }
                },
                children: /*#__PURE__*/ _jsx("span", {
                    "aria-hidden": "true",
                    children: "?"
                })
            }),
            open && /*#__PURE__*/ _jsx(OverlayPortal, {
                children: /*#__PURE__*/ _jsx("span", {
                    ref: bubble,
                    id: bubbleId,
                    role: "tooltip",
                    className: cx('rhd-helptip__bubble', pos?.below && 'rhd-helptip__bubble--below'),
                    "data-testid": name ? Testids.app.help.tipBubble(name) : undefined,
                    style: pos ? {
                        top: pos.top,
                        left: pos.left
                    } : {
                        top: 0,
                        left: 0,
                        visibility: 'hidden'
                    },
                    onMouseEnter: show,
                    onMouseLeave: hideSoon,
                    children: children
                })
            })
        ]
    });
}
